import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2 } from 'lucide-react';

/**
 * ScenariosSheet Component
 * 
 * Data Flow:
 * - Scenario Multipliers Table: Editable, writes back to sheet (rows 5-7)
 * - Active Scenario: Editable dropdown, writes to sheet (column B, Active Scenario row)
 * - ScenarioRowIndex: READ-ONLY, only fetches from sheet, NEVER modified by frontend
 * 
 * When Active Scenario changes:
 * 1. Frontend updates only the Active Scenario cell in column B
 * 2. Google Sheet formulas may update ScenarioRowIndex automatically
 * 3. Frontend re-fetches data to display updated ScenarioRowIndex value
 * 4. ScenarioRowIndex is NEVER written by this component - it's purely read-only
 */
const ScenariosSheet = ({ hasAccess, onRequestAccess }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scenarios, setScenarios] = useState([
    { scenario: 'Base', price: '1', volume: '1', cogsPercent: '1', fixedOpex: '1', varOpexPercent: '1', smPercent: '1' },
    { scenario: 'Best', price: '1.05', volume: '1.1', cogsPercent: '0.95', fixedOpex: '1', varOpexPercent: '0.95', smPercent: '0.95' },
    { scenario: 'Worst', price: '0.95', volume: '0.9', cogsPercent: '1.05', fixedOpex: '1.05', varOpexPercent: '1.1', smPercent: '1.1' }
  ]);
  const [activeScenario, setActiveScenario] = useState('Base');
  const [scenarioRowIndex, setScenarioRowIndex] = useState('1');

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/sheets/Scenarios`);
      const data = response.data.data;
      
      // Find Scenario Multipliers table
      let startRow = -1;
      for (let i = 0; i < data.length; i++) {
        if (data[i][0] === 'Scenario' && data[i][1] === 'Price') {
          startRow = i + 1;
          break;
        }
      }
      
      if (startRow > 0) {
        const parsedScenarios = [];
        for (let i = startRow; i < startRow + 3 && i < data.length; i++) {
          const row = data[i];
          if (row[0]) {
            parsedScenarios.push({
              scenario: row[0],
              price: row[1] || '',
              volume: row[2] || '',
              cogsPercent: row[3] || '',
              fixedOpex: row[4] || '',
              varOpexPercent: row[5] || '',
              smPercent: row[6] || ''
            });
          }
        }
        if (parsedScenarios.length > 0) setScenarios(parsedScenarios);
      }
      
      // Fetch Active Scenario and ScenarioRowIndex (READ-ONLY from sheet)
      // These values are only retrieved, never written by this component
      for (let i = 0; i < data.length; i++) {
        if (data[i][0] === 'Active Scenario') {
          // Active Scenario: READ from sheet, can be WRITTEN back via dropdown
          setActiveScenario(data[i][1] || 'Base');
        }
        if (data[i][0] === 'ScenarioRowIndex') {
          // ScenarioRowIndex: READ-ONLY from sheet, NEVER written by frontend
          setScenarioRowIndex(data[i][1] || '1');
        }
      }
    } catch (error) {
      console.error('Failed to load scenarios:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index, field, value) => {
    const updated = [...scenarios];
    updated[index][field] = value;
    setScenarios(updated);
  };

  const autoSave = async () => {
    if (!hasAccess) {
      onRequestAccess(); // Show payment dialog
      return;
    }
    setSaving(true);
    try {
      const values = scenarios.map(s => [
        s.scenario,
        s.price,
        s.volume,
        s.cogsPercent,
        s.fixedOpex,
        s.varOpexPercent,
        s.smPercent
      ]);

      await axios.post(`${API}/sheets/update`, {
        sheet_name: 'Scenarios',
        range: 'A5:G7',
        values: values
      });
      
      toast.success('Saved successfully');
      setTimeout(fetchData, 1000);
    } catch (error) {
      toast.error('Auto-save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleActiveScenarioChange = async (value) => {
    if (!hasAccess) {
      onRequestAccess(); // Show payment dialog
      return;
    }
    setActiveScenario(value);
    setSaving(true);
    try {
      // CRITICAL: Only update Active Scenario cell in the sheet
      // ScenarioRowIndex is NEVER modified by the frontend - it's read-only
      const activeScenarioRow = await findActiveScenarioRow();
      
      await axios.post(`${API}/sheets/update`, {
        sheet_name: 'Scenarios',
        range: `B${activeScenarioRow}`,  // Only update column B of the Active Scenario row
        values: [[value]]
      });
      
      toast.success('Active scenario updated');
      // Refresh data to get updated ScenarioRowIndex from sheet (if it changed due to sheet formulas)
      setTimeout(fetchData, 1000);
    } catch (error) {
      toast.error('Failed to update');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  // Helper function to find the row number of "Active Scenario" in the sheet
  const findActiveScenarioRow = async () => {
    try {
      const response = await axios.get(`${API}/sheets/Scenarios`);
      const data = response.data.data;
      
      for (let i = 0; i < data.length; i++) {
        if (data[i][0] === 'Active Scenario') {
          return i + 1; // Convert 0-based index to 1-based row number
        }
      }
      return 10; // Default fallback to row 10 if not found
    } catch (error) {
      console.error('Error finding Active Scenario row:', error);
      return 10; // Default fallback
    }
  };

  if (loading) {
    return <div className="p-6 text-slate-600">Loading scenarios...</div>;
  }

  return (
    <div className="space-y-6 fade-in">
      {saving && (
        <div className="fixed top-20 right-6 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 z-50">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Saving...</span>
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-800 mb-6">Scenario Multipliers</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-slate-200">
                <th className="text-left py-3 px-4 font-semibold text-slate-700">Scenario</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-700">Price</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-700">Volume</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-700">COGS %</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-700">Fixed Opex</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-700">Var Opex %</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-700">S&M %</th>
              </tr>
            </thead>
            <tbody>
              {scenarios.map((scenario, index) => (
                <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4 font-medium text-slate-700">{scenario.scenario}</td>
                  <td className="py-3 px-4">
                    <Input
                      type="number"
                      step="0.01"
                      value={scenario.price}
                      onChange={(e) => handleChange(index, 'price', e.target.value)}
                      onBlur={() => autoSave()}
                      className="h-9"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <Input
                      type="number"
                      step="0.01"
                      value={scenario.volume}
                      onChange={(e) => handleChange(index, 'volume', e.target.value)}
                      onBlur={() => autoSave()}
                      className="h-9"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <Input
                      type="number"
                      step="0.01"
                      value={scenario.cogsPercent}
                      onChange={(e) => handleChange(index, 'cogsPercent', e.target.value)}
                      onBlur={() => autoSave()}
                      className="h-9"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <Input
                      type="number"
                      step="0.01"
                      value={scenario.fixedOpex}
                      onChange={(e) => handleChange(index, 'fixedOpex', e.target.value)}
                      onBlur={() => autoSave()}
                      className="h-9"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <Input
                      type="number"
                      step="0.01"
                      value={scenario.varOpexPercent}
                      onChange={(e) => handleChange(index, 'varOpexPercent', e.target.value)}
                      onBlur={() => autoSave()}
                      className="h-9"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <Input
                      type="number"
                      step="0.01"
                      value={scenario.smPercent}
                      onChange={(e) => handleChange(index, 'smPercent', e.target.value)}
                      onBlur={() => autoSave()}
                      className="h-9"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Active Scenario</label>
            <Select value={activeScenario} onValueChange={handleActiveScenarioChange}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Base">Base</SelectItem>
                <SelectItem value="Best">Best</SelectItem>
                <SelectItem value="Worst">Worst</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              ScenarioRowIndex <span className="text-xs text-slate-500">(Read-only from sheet)</span>
            </label>
            <Input
              value={scenarioRowIndex}
              disabled
              readOnly
              className="bg-slate-100 cursor-not-allowed"
              title="This value is read-only and fetched directly from the Google Sheet"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScenariosSheet;
